/**
 * Frontend Application - Sistema modernizado sin jQuery
 * Oasis Media Center Frontend
 * 
 * Este archivo reemplaza completamente la dependencia de jQuery
 * manteniendo toda la funcionalidad existente.
 */

// Importar axios (ya disponible desde el backend)
import axios from 'axios';

// Importar SweetAlert2
import { AlertManager, Swal } from '../sweetalert-config.js';

// Importar módulos modernos del frontend
import { FrontendDropdown } from './dropdown.js';
import { FavoritesManager } from './favorites.js';
import { FrontendLightbox } from './lightbox-config.js';
import { UIEffects } from './ui-effects.js';
import './layout-globals.js'; // Flash messages, global helpers, toggleFavorite, GA
import './pages/rate-limit.js'; // Rate limit countdown timer
import './pages/frontend-utils.js'; // Language switcher, modal helpers, avatar preview
import './pages/favorites-page.js'; // Favorites page: removeFavorite, downloadFile
import './pages/history-page.js'; // History page: downloadFile (toggleFavorite from layout-globals)
import './pages/login-page.js'; // Login page: error modals, resend activation, password reset
import './pages/recaptcha-form.js'; // reCAPTCHA Enterprise: register + recovery form submission

class FrontendApp {
    constructor() {
        this.components = {};
        this.isInitialized = false;
        this.init();
    }

    init() {
        // Configurar axios globalmente
        this.setupAxios();
        
        // Configurar variables globales para compatibilidad
        this.setupGlobalVariables();
        
        // Esperar a que el DOM esté listo
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeComponents();
            });
        } else {
            this.initializeComponents();
        }
    }

    setupAxios() {
        // Hacer axios disponible globalmente
        window.axios = axios;
        
        // Configurar CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
            axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }
        
        // Configurar base URL
        const baseUrl = window.base_url || window.location.origin;
        axios.defaults.baseURL = baseUrl;
        
        console.log('Axios configurado para frontend');
    }

    setupGlobalVariables() {
        // Variables de compatibilidad - read from meta tags first, fallback to origin
        if (!window.base_url) {
            const baseUrlMeta = document.querySelector('meta[name="base-url"]');
            window.base_url = baseUrlMeta ? baseUrlMeta.content : window.location.origin;
        }

        // Función global getCurrentLocale para compatibilidad
        window.getCurrentLocale = function() {
            const path = window.location.pathname;
            const matches = path.match(/^\/?([a-z]{2})\//);
            return matches ? matches[1] : 'es';
        };
    }

    initializeComponents() {
        if (this.isInitialized) return;
        
        console.log('🚀 Iniciando Frontend App - Sistema modernizado');
        
        try {
            // Inicializar componentes principales
            this.components.dropdown = new FrontendDropdown('.ddm');
            // this.components.favorites = new FavoritesManager(); // Comentado temporalmente para evitar conflictos
            this.components.uiEffects = new UIEffects();
            this.components.alerts = AlertManager;
            
            // Inicializar lightbox después de un pequeño delay para asegurar que esté cargado
            setTimeout(() => {
                this.components.lightbox = new FrontendLightbox();
            }, 100);
            
            // Configurar eventos globales
            this.setupGlobalEvents();
            
            this.isInitialized = true;
            console.log('✅ Todos los componentes del frontend inicializados');
            
            // Dispatch evento personalizado
            document.dispatchEvent(new CustomEvent('frontend:ready', {
                detail: { 
                    app: this,
                    components: this.components 
                }
            }));
            
        } catch (error) {
            console.error('❌ Error inicializando componentes:', error);
        }
    }

    setupGlobalEvents() {
        // Evento para refrescar componentes cuando se carga contenido dinámico
        document.addEventListener('content:loaded', () => {
            this.refreshComponents();
        });

        // Manejo de errores AJAX globales
        window.addEventListener('unhandledrejection', (event) => {
            console.error('Promise rejection no manejada:', event.reason);
        });

        // Debug: Log clicks para debugging
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
            document.addEventListener('click', (e) => {
                const target = e.target.closest('[class*="add"], [class*="remove"], [class*="ddm"]');
                if (target) {
                    console.log('Debug - Click en:', target.className, target);
                }
            });
        }
    }

    /**
     * Refrescar todos los componentes
     */
    refreshComponents() {
        console.log('🔄 Refrescando componentes del frontend');
        
        Object.keys(this.components).forEach(key => {
            const component = this.components[key];
            if (component && typeof component.refresh === 'function') {
                try {
                    component.refresh();
                    console.log(`✅ ${key} refrescado`);
                } catch (error) {
                    console.error(`❌ Error refrescando ${key}:`, error);
                }
            }
        });
    }

    /**
     * Obtener información del estado de la aplicación
     */
    getStatus() {
        const status = {
            initialized: this.isInitialized,
            components: {},
            globals: {
                baseUrl: window.base_url,
                locale: window.getCurrentLocale?.(),
                axiosConfigured: !!window.axios
            }
        };

        // Estado de cada componente
        Object.keys(this.components).forEach(key => {
            const component = this.components[key];
            status.components[key] = {
                initialized: component?.isInitialized || false,
                type: component?.constructor?.name || 'Unknown'
            };
        });

        return status;
    }

    /**
     * Método para recargar la aplicación
     */
    reload() {
        console.log('🔄 Recargando Frontend App');
        this.isInitialized = false;
        this.components = {};
        this.initializeComponents();
    }

    /**
     * Método para limpiar la aplicación
     */
    destroy() {
        console.log('🧹 Destruyendo Frontend App');
        
        // Destruir componentes que lo soporten
        Object.keys(this.components).forEach(key => {
            const component = this.components[key];
            if (component && typeof component.destroy === 'function') {
                component.destroy();
            }
        });
        
        this.components = {};
        this.isInitialized = false;
    }
}

// Inicializar aplicación
const frontendApp = new FrontendApp();

// Hacer la aplicación disponible globalmente para debugging
window.frontendApp = frontendApp;

// Hacer AlertManager disponible globalmente
window.AlertManager = AlertManager;
window.Swal = Swal;

// Funciones globales de compatibilidad con el código existente
window.previewImage = function(input) {
    if (frontendApp.components.uiEffects) {
        frontendApp.components.uiEffects.previewImage(input);
    }
};

// downloadFile is now defined per-page (favorites-page.js, history-page.js)
// with fallback to UIEffects.downloadFile(tipo, id) for media pages

// Export para testing o uso modular
export default frontendApp;
export { FrontendApp };

console.log('📦 Frontend App cargado - Sistema completamente modernizado');
